import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  LinearProgress,
  Alert,
  Grid,
  Paper,
  Divider,
  useTheme,
  alpha,
  Tabs,
  Tab,
  TextField,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Stack,
  ButtonGroup,
  Tooltip,
} from "@mui/material";
import {
  CloudUpload as UploadIcon,
  MoreVert as MoreIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  FormatListNumbered,
  DoneAll,
  Error,
  ContentCopy,
  Update,
  ErrorOutline,
  Search as SearchIcon,
} from "@mui/icons-material";
import { useDropzone } from "react-dropzone";
import { useSnackbar } from "notistack";
import { useDispatch, useSelector } from "react-redux";
import {
  importFlipkartOrders,
  getFlipkartOrderHistoryList,
} from "app/redux/slice/flipkartSlice";
import { Table as CustomTable } from "app/components";
import { usePagination } from "app/hooks/usePagination";
import Loader from "@jumbo/components/Loader";
import moment from "moment";
import { LocalizationProvider, DateRangePicker } from "@mui/lab";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import { userType } from "app/helpers/userType";

const QUICK_RANGES = [
  {
    key: "today",
    label: "Today",
    getRange: () => [
      moment().startOf("day").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
  {
    key: "7d",
    label: "Last 7D",
    getRange: () => [
      moment().subtract(6, "days").startOf("day").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
  {
    key: "30d",
    label: "Last 30D",
    getRange: () => [
      moment().subtract(29, "days").startOf("day").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
  {
    key: "ytd",
    label: "YTD",
    getRange: () => [
      moment().startOf("year").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
];

const statusOptions = [
  { value: "completed", label: "Completed", color: "success" },
  { value: "failed", label: "Failed", color: "error" },
  { value: "partial", label: "Partial", color: "warning" },
];

const FlipkartImport = () => {
  const theme = useTheme();
  const { isMA } = userType();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const [activeTab, setActiveTab] = useState("upload");

  // Upload state
  const [uploading, setUploading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  // History state
  const flipkartData = useSelector((state) => state?.flipkart);
  const historyList = flipkartData?.orderHistory?.list ?? [];

  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({ status: "" });
  const [dateRange, setDateRange] = useState([null, null]);
  const [tempSearchTerm, setTempSearchTerm] = useState("");
  const [tempFilters, setTempFilters] = useState({ status: "" });
  const [tempDateRange, setTempDateRange] = useState([null, null]);

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [rowDetails, setRowDetails] = useState(null);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("createdAt");

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    usePagination({ id: "flipkart-import-history" });

  const [permissionData] = useState(
    JSON.parse(localStorage.getItem("permission")),
  );

  const [permission, setPermission] = useState({
    historyPermission: false,
  });

  const getPermission = () => {
    if (isMA) {
      setPermission({
        historyPermission: true,
      });
    } else {
      let permissionValue = {
        historyPermission: false,
      };
      permissionData?.length &&
        permissionData.forEach(function (num) {
          if (num?.name === "flipkart-import-history") {
            permissionValue.historyPermission = true;
          }
        });
      setPermission(permissionValue);
    }
  };

  useEffect(() => {
    getPermission();
  }, []);

  // Dropzone
  const onDrop = (acceptedFiles) => {
    if (acceptedFiles && acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      const isValid =
        file.type === "text/csv" ||
        file.name.endsWith(".csv") ||
        file.name.endsWith(".xlsx") ||
        file.name.endsWith(".xls");
      if (!isValid) {
        enqueueSnackbar("Please select a valid CSV or Excel file", {
          variant: "error",
        });
        return;
      }
      setSelectedFile(file);
      setImportResult(null);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: "text/csv,.csv,.xlsx,.xls",
    multiple: false,
  });

  const handleImport = async () => {
    if (!selectedFile) {
      enqueueSnackbar("Please select a file first", { variant: "warning" });
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      const response = await dispatch(importFlipkartOrders(formData)).unwrap();

      if (response?.data?.statusCode === 200) {
        setImportResult(response?.data?.data?.summary);
        enqueueSnackbar("Data imported successfully!", { variant: "success" });
      } else {
        console.log(response);

        enqueueSnackbar(response?.data?.responseMessage || "Import failed", {
          variant: "error",
        });
      }
    } catch (error) {
      console.log(error);

      enqueueSnackbar(
        error?.response?.data?.message || "Failed to import data",
        { variant: "error" },
      );
    } finally {
      setUploading(false);
    }
  };

  // History fetch
  useEffect(() => {
    if (activeTab === "history") {
      fetchHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTab,
    page,
    rowsPerPage,
    searchTerm,
    filters,
    dateRange,
    order,
    orderBy,
  ]);

  const fetchHistory = () => {
    const params = {
      page,
      perPage: rowsPerPage,
      search: searchTerm.trim(),
    };

    // Only add filter keys if they have a non-empty value
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== "" && value !== null && value !== undefined) {
        params[key] = value;
      }
    });

    if (dateRange[0] !== null && dateRange[1] !== null) {
      params.fromDate = moment(dateRange[0]).format("YYYY-MM-DD");
      params.toDate = moment(dateRange[1]).format("YYYY-MM-DD");
    }
    dispatch(getFlipkartOrderHistoryList(params));
  };

  const applyFilters = () => {
    setSearchTerm(tempSearchTerm);
    setFilters({ ...tempFilters });
    setDateRange(tempDateRange);
    handleChangePage(0, 0);
  };

  const clearAll = () => {
    setTempSearchTerm("");
    setSearchTerm("");
    setTempFilters({ status: "" });
    setFilters({ status: "" });
    setTempDateRange([null, null]);
    setDateRange([null, null]);
    handleChangePage(0, 0);
  };

  const getStatusChip = (status) => {
    const cfg = statusOptions.find((o) => o.value === status);
    return (
      <Chip
        label={cfg?.label || status}
        color={cfg?.color || "default"}
        size="small"
      />
    );
  };

  const historyColumns = [
    {
      id: "originalFileName",
      label: "File Name",
      sort: false,
      render: (row) => (
        <Tooltip title={row?.originalFileName}>
          <Typography variant="body2" noWrap sx={{ maxWidth: 250 }}>
            {row?.originalFileName}
          </Typography>
        </Tooltip>
      ),
    },
    {
      id: "createdAt",
      label: "Date",
      sort: false,
      render: (row) => (
        <>{moment(row?.createdAt).format("DD/MM/YYYY hh:mm A")}</>
      ),
    },
    {
      id: "totalRecords",
      label: "Total",
      sort: false,
    },
    {
      id: "duplicateRecords",
      label: "Duplicate",
      sort: false,
    },
    {
      id: "successfulImports",
      label: "Successful",
      sort: false,
    },
    {
      id: "failedImports",
      label: "Failed",
      sort: false,
    },
    {
      id: "skipRecords",
      label: "Skip",
      sort: false,
    },
    {
      id: "updatedRecords",
      label: "Updated",
      sort: false,
    },
    {
      id: "status",
      label: "Status",
      sort: false,
      render: (row) => <>{getStatusChip(row?.status)}</>,
    },
    {
      id: "actions",
      label: "Actions",
      align: "right",
      render: (row) => (
        <IconButton
          onClick={(e) => {
            setAnchorEl(e.currentTarget);
            setSelectedRow(row);
          }}
        >
          <MoreIcon />
        </IconButton>
      ),
    },
  ];

  const statCards = [
    {
      label: "Total",
      value: rowDetails?.totalRecords,
      icon: <FormatListNumbered />,
    },
    {
      label: "Success",
      value: rowDetails?.successfulImports,
      icon: <DoneAll />,
      color: "success",
    },
    {
      label: "Updated",
      value: rowDetails?.updatedRecords,
      icon: <Update />,
      color: "info",
    },
    {
      label: "Duplicates",
      value: rowDetails?.duplicateRecords,
      icon: <ContentCopy />,
      color: "warning",
    },
    {
      label: "Skipped",
      value: rowDetails?.skipRecords,
      icon: <ErrorOutline />,
      color: "secondary",
    },
    {
      label: "Failed",
      value: rowDetails?.failedImports,
      icon: <Error />,
      color: "error",
    },
  ];

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Flipkart Import
      </Typography>

      {/* Main Tabs: Upload / History */}
      <Tabs
        value={activeTab}
        onChange={(e, v) => setActiveTab(v)}
        sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}
      >
        <Tab value="upload" label="Upload File" />
        <Tab value="history" label="Import History" disabled={!permission.historyPermission} />
      </Tabs>

      {/* ──────────────── UPLOAD TAB ──────────────── */}
      {activeTab === "upload" && (
        <Box>
          <Grid container spacing={3}>
            {/* Dropzone */}
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Box
                    {...getRootProps()}
                    sx={{
                      border: `2px dashed ${
                        isDragActive
                          ? theme.palette.primary.main
                          : theme.palette.grey[300]
                      }`,
                      borderRadius: 2,
                      p: 4,
                      textAlign: "center",
                      cursor: "pointer",
                      backgroundColor: isDragActive
                        ? alpha(theme.palette.primary.main, 0.05)
                        : "transparent",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        backgroundColor: alpha(
                          theme.palette.primary.main,
                          0.05,
                        ),
                      },
                    }}
                  >
                    <input {...getInputProps()} />
                    <UploadIcon
                      sx={{
                        fontSize: 48,
                        color: theme.palette.primary.main,
                        mb: 2,
                      }}
                    />
                    <Typography variant="h6" gutterBottom>
                      {isDragActive
                        ? "Drop the CSV/Excel file here"
                        : "Drag & drop a CSV or Excel file here"}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      or click to select a file
                    </Typography>
                    <Button variant="outlined" disabled={uploading}>
                      Select File
                    </Button>
                  </Box>

                  {selectedFile && (
                    <Paper
                      sx={{
                        p: 2,
                        mt: 2,
                        backgroundColor: alpha(
                          theme.palette.primary.main,
                          0.05,
                        ),
                      }}
                    >
                      <Typography variant="subtitle2" gutterBottom>
                        Selected File:
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedFile.name} (
                        {(selectedFile.size / 1024).toFixed(2)} KB)
                      </Typography>
                    </Paper>
                  )}

                  {uploading && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" gutterBottom>
                        Importing...
                      </Typography>
                      <LinearProgress />
                    </Box>
                  )}

                  <Box
                    sx={{
                      mt: 3,
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <Stack direction="row" spacing={2}>
                      <Button
                        variant="contained"
                        onClick={handleImport}
                        disabled={!selectedFile || uploading}
                        startIcon={<UploadIcon />}
                      >
                        {uploading ? "Importing..." : "Import Data"}
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setSelectedFile(null);
                          setImportResult(null);
                        }}
                        disabled={uploading}
                      >
                        Clear
                      </Button>
                    </Stack>
                    <Button
                      variant="text"
                      onClick={() => setActiveTab("history")}
                      disabled={!permission.historyPermission}
                    >
                      View History →
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Instructions */}
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Import Instructions
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Flipkart Settlement Report (Excel):
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      component="div"
                    >
                      • Order Date
                      <br />
                      • Order ID
                      <br />
                      • Order Item ID
                      <br />
                      • SKU Name
                      <br />
                    </Typography>
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Same orderId and OrderItemId duplicate will update
                    </Alert>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Import Results */}
          {importResult && (
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Import Results
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  {[
                    {
                      label: "Total Records",
                      value: importResult.totalRecords,
                      color: "primary",
                    },
                    {
                      label: "Imported",
                      value: importResult.successfulImports,
                      color: "success.main",
                    },
                    {
                      label: "Updated",
                      value: importResult.updatedRecords ?? 0,
                      color: "info.main",
                    },
                    {
                      label: "Duplicates",
                      value: importResult.duplicateRecords,
                      color: "warning.main",
                    },
                    {
                      label: "Skipped",
                      value: importResult.skipRecords ?? 0,
                      color: "secondary.main",
                    },
                    {
                      label: "Failed",
                      value: importResult.failedImports,
                      color: "error.main",
                    },
                  ].map((item, i) => (
                    <Grid item xs={12} sm={6} md={2.4} key={i}>
                      <Paper sx={{ p: 2, textAlign: "center" }}>
                        <Typography variant="h4" color={item.color}>
                          {item.value}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {item.label}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>

                {importResult?.issueDetails?.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Error Details:
                    </Typography>
                    <Box sx={{ maxHeight: 200, overflow: "auto" }}>
                      {importResult?.issueDetails?.map((err, i) => (
                        <Alert key={i} severity="error" sx={{ mb: 1 }}>
                          Row {err.row}: {err.error}
                        </Alert>
                      ))}
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}
        </Box>
      )}

      {/* ──────────────── HISTORY TAB ──────────────── */}
      {activeTab === "history" && (
        <Box>
          {/* Filters */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search by file name..."
                    value={tempSearchTerm}
                    onChange={(e) => setTempSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={2.75}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DateRangePicker
                      startText="From Date"
                      endText="To Date"
                      inputFormat="dd/MM/yyyy"
                      value={tempDateRange}
                      onChange={setTempDateRange}
                      renderInput={(
                        { inputProps, ...startProps },
                        endProps,
                      ) => {
                        const startValue = inputProps.value;
                        delete inputProps.value;
                        return (
                          <TextField
                            {...startProps}
                            inputProps={inputProps}
                            value={`${startValue || "DD/MM/YYYY"} - ${
                              endProps.inputProps.value || "DD/MM/YYYY"
                            }`}
                            fullWidth
                            size="small"
                          />
                        );
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <ButtonGroup size="small" variant="outlined" fullWidth>
                    {QUICK_RANGES.map((r) => (
                      <Button
                        key={r.key}
                        onClick={() => setTempDateRange(r.getRange())}
                      >
                        {r.label}
                      </Button>
                    ))}
                  </ButtonGroup>
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={tempFilters.status}
                      onChange={(e) =>
                        setTempFilters((p) => ({
                          ...p,
                          status: e.target.value,
                        }))
                      }
                      label="Status"
                    >
                      <MenuItem value="">All</MenuItem>
                      {statusOptions.map((o) => (
                        <MenuItem key={o.value} value={o.value}>
                          {o.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="contained"
                      onClick={applyFilters}
                      fullWidth
                      size="small"
                    >
                      Apply Filters
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={clearAll}
                      startIcon={<RefreshIcon />}
                      fullWidth
                      size="small"
                    >
                      Clear
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* History Table */}
          {flipkartData?.orderHistory?.loading ? (
            <Loader />
          ) : (
            <Card>
              <CardContent>
                <CustomTable
                  columns={historyColumns}
                  rows={historyList}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  count={
                    flipkartData?.orderHistory?.total || historyList.length
                  }
                  Order={order}
                  OrderBy={orderBy}
                  sortData={(o, ob) => {
                    setOrder(o);
                    setOrderBy(ob);
                  }}
                  handleChangePage={handleChangePage}
                  handleChangeRowsPerPage={handleChangeRowsPerPage}
                  loading={flipkartData?.orderHistory?.loading}
                />
              </CardContent>
            </Card>
          )}

          {/* Row Action Menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
          >
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                if (selectedRow?._id) {
                  setRowDetails(selectedRow);
                  setDetailsDialog(true);
                }
              }}
            >
              <ViewIcon sx={{ mr: 1 }} />
              View Details
            </MenuItem>
          </Menu>

          {/* Details Dialog */}
          <Dialog
            open={detailsDialog}
            onClose={() => setDetailsDialog(false)}
            maxWidth="lg"
            fullWidth
          >
            <DialogTitle>Import History Details</DialogTitle>
            <DialogContent dividers>
              <Box mb={2}>
                <Typography variant="subtitle1" fontWeight={600}>
                  File: {rowDetails?.originalFileName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Imported By: <strong>{rowDetails?.importedBy?.name}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Date:{" "}
                  {moment
                    .utc(rowDetails?.createdAt)
                    .format("DD/MM/YYYY hh:mm A")}
                </Typography>
              </Box>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                {statCards.map((item, index) => (
                  <Grid key={index} item xs={6} sm={4} md={2}>
                    <Card
                      sx={{
                        textAlign: "center",
                        borderRadius: 3,
                        background:
                          item.color === "success"
                            ? "#E8F5E9"
                            : item.color === "error"
                            ? "#FFEBEE"
                            : item.color === "warning"
                            ? "#FFF8E1"
                            : item.color === "info"
                            ? "#E3F2FD"
                            : "#F5F5F5",
                      }}
                      elevation={0}
                    >
                      <CardContent>
                        <Box display="flex" justifyContent="center" mb={1}>
                          {item.icon}
                        </Box>
                        <Typography variant="h6" fontWeight={700}>
                          {item?.value ?? 0}
                        </Typography>
                        <Typography variant="body2">{item?.label}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {rowDetails?.issueDetails?.length > 0 && (
                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    Error Details:
                  </Typography>
                  <Box sx={{ maxHeight: 200, overflow: "auto" }}>
                    {rowDetails.issueDetails.map((err, i) => (
                      <Alert key={i} severity="error" sx={{ mb: 1 }}>
                        Row {err?.row}: {err?.error}
                      </Alert>
                    ))}
                  </Box>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailsDialog(false)}>Close</Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}
    </Box>
  );
};

export default FlipkartImport;
